import {DocumentNode, gql} from '@apollo/client';
import React from 'react';

import DataConnector from 'connect/DataConnector';
import {PlateSchema, PlateSchemaType, StorableSchema} from 'samples/storables/StorableSchemaConstants';
import {SchemaOwnerQuery} from 'schemas/SchemaOwnerUtil';

import {PlateSchemaSelectorOwnerQuery} from './__generated/PlateSchemaSelectorOwnerQuery';
import * as SampleSchemaActions from './SampleSchemaActions';
import SampleSchemaStore from './SampleSchemaStore';
import StorableSchemaSelector from './StorableSchemaSelector';

export interface PlateSchemaSelectorProps {
  disabled?: boolean;
  disabledMessage?: string;
  error?: string;
  // Called with `(schema | null)` if `shouldShowNoSchemaOption` is true.  Otherwise, called with `(schema)`.
  onChange: (schema: StorableSchema | null) => void;
  registryId?: string;
  plateSchemaType?: PlateSchemaType;
  selectedPlateSchemaId: string | null;
  // Marked as optional here but there is a default prop for it so we can rely on it being present in the component.
  shouldShowNoSchemaOption?: boolean;
  plateSchemas?: Array<{
    id: string;
    name: string;
  }>;
  /** Function to filter which plate schemas to show in the dropdown in addition to plateSchemaType */
  plateSchemaFilter?: (schema: PlateSchema) => boolean;
}

interface PlateSchemaSelectorEnhancedProps {
  plateSchemas: Array<PlateSchema> | null;
}

class PlateSchemaSelectorConnector extends DataConnector<
  PlateSchemaSelectorProps,
  PlateSchemaSelectorEnhancedProps
> {
  override stores = [SampleSchemaStore];

  enhanceProps(): PlateSchemaSelectorEnhancedProps {
    const {registryId, plateSchemaType, plateSchemaFilter} = this.props;
    let plateSchemas = this.sampleSchemaStore().getPlateSchemas(registryId);
    if (plateSchemaType && plateSchemas) {
      plateSchemas = plateSchemas.filter((schema) => schema.plateSchemaType === plateSchemaType);
    }
    if (plateSchemaFilter != null && plateSchemas) {
      plateSchemas = plateSchemas.filter((schema) => plateSchemaFilter(schema));
    }
    return {plateSchemas};
  }
}

export const PLATE_SCHEMA_SELECTOR_OWNER_QUERY: DocumentNode = gql`
  query PlateSchemaSelectorOwnerQuery @skipAudit @engComponent(name: INVENTORY) {
    plateSchemas {
      id
      registry {
        id
        organization {
          id
          name
        }
      }
    }
  }
`;

class PlateSchemaSelector extends React.Component<
  PlateSchemaSelectorProps & PlateSchemaSelectorEnhancedProps
> {
  static defaultProps = {
    shouldShowNoSchemaOption: false,
  };

  override componentDidMount(): void {
    SampleSchemaActions.fetchAllPlateSchemas();
  }

  override render(): JSX.Element {
    return (
      <SchemaOwnerQuery<PlateSchemaSelectorOwnerQuery>
        query={PLATE_SCHEMA_SELECTOR_OWNER_QUERY}
        getIdOwnerTuples={(data) =>
          data.plateSchemas.map((plateSchema) => ({
            id: plateSchema.id,
            owner: plateSchema.registry.organization.name,
          }))
        }
      >
        {({getSchemaOwner}) => (
          <StorableSchemaSelector
            isDisabled={this.props.disabled}
            disabledMessage={this.props.disabledMessage}
            error={this.props.error}
            onChange={this.props.onChange}
            schemas={this.props.plateSchemas}
            selectedSchemaId={this.props.selectedPlateSchemaId}
            shouldShowNoSchemaOption={this.props.shouldShowNoSchemaOption}
            getSchemaOwner={getSchemaOwner}
            // Overridden by props from redux
            starredSchemaIds={[]}
          />
        )}
      </SchemaOwnerQuery>
    );
  }
}

export default PlateSchemaSelectorConnector.connect(PlateSchemaSelector);
